import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from './Dashboard';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { Plus, Trash2, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';

const COLORS = [
  { value: '#ef4444', label: 'Red' },
  { value: '#f97316', label: 'Orange' },
  { value: '#eab308', label: 'Yellow' },
  { value: '#22c55e', label: 'Green' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#6b7280', label: 'Gray' }
];

const Transactions = () => {
  const { user } = useAuth();
  const isWorker = user?.role === 'worker';
  const [transactions, setTransactions] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [filterEstablishment, setFilterEstablishment] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newTransaction, setNewTransaction] = useState({
    type: 'expense',
    price: 0,
    currency: 'EUR',
    payment_method: 'cash',
    note: '',
    color: '#ef4444',
    date: new Date().toISOString().split('T')[0],
    establishment_id: user?.establishment_id || '',
    establishment_name: user?.establishment_name || ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [txnRes, estRes] = await Promise.all([
        axios.get(`${API}/transactions`),
        axios.get(`${API}/establishments`)
      ]);
      setTransactions(txnRes.data);
      setEstablishments(estRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const createTransaction = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/transactions`, newTransaction);
      toast.success('Transaction created!');
      setShowModal(false);
      setNewTransaction({
        type: isWorker ? 'expense' : 'expense',
        price: 0,
        currency: 'EUR',
        payment_method: 'cash',
        note: '',
        color: '#ef4444',
        date: new Date().toISOString().split('T')[0],
        establishment_id: user?.establishment_id || '',
        establishment_name: user?.establishment_name || ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create transaction');
    }
  };

  const deleteTransaction = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await axios.delete(`${API}/transactions/${id}`);
      toast.success('Transaction deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete transaction');
    }
  };

  const filteredTransactions = filterEstablishment
    ? transactions.filter(t => t.establishment_id === filterEstablishment)
    : transactions;
  const expenses = filteredTransactions.filter(t => t.type === 'expense');
  const income = filteredTransactions.filter(t => t.type === 'income');
  const totalExpenses = expenses.reduce((sum, t) => sum + t.price_dt, 0);
  const totalIncome = income.reduce((sum, t) => sum + t.price_dt, 0);
  const balance = totalIncome - totalExpenses;

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-3xl font-bold text-gray-900">{isWorker ? 'Expenses' : 'Transactions'}</h2>
          <div className="flex items-center space-x-3">
            {!isWorker && (
              <select
                value={filterEstablishment}
                onChange={(e) => setFilterEstablishment(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                data-testid="transactions-establishment-filter"
              >
                <option value="">All Establishments</option>
                {establishments.map((est) => (
                  <option key={est.id} value={est.id}>{est.name}</option>
                ))}
              </select>
            )}
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              <Plus className="w-4 h-4" />
              <span>{isWorker ? 'Add Expense' : 'New Transaction'}</span>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl shadow-sm p-6 border border-red-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-red-900">Total Expenses</h3>
              <TrendingDown className="w-5 h-5 text-red-600" />
            </div>
            <p className="text-3xl font-bold text-red-900">{totalExpenses.toFixed(2)} DT</p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm p-6 border border-green-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-green-900">Total Income</h3>
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-green-900">{totalIncome.toFixed(2)} DT</p>
          </div>

          <div className={`bg-gradient-to-br ${balance >= 0 ? 'from-blue-50 to-blue-100 border-blue-200' : 'from-orange-50 to-orange-100 border-orange-200'} rounded-xl shadow-sm p-6 border`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-sm font-medium ${balance >= 0 ? 'text-blue-900' : 'text-orange-900'}`}>Balance</h3>
              <DollarSign className={`w-5 h-5 ${balance >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
            </div>
            <p className={`text-3xl font-bold ${balance >= 0 ? 'text-blue-900' : 'text-orange-900'}`}>
              {balance.toFixed(2)} DT
            </p>
          </div>
        </div>

        {/* Transactions List */}
        <div className="space-y-3">
          {filteredTransactions.map((transaction) => (
            <div
              key={transaction.id}
              className="bg-white rounded-xl shadow-sm p-4 border border-gray-200 flex items-center justify-between"
              style={{ borderLeftWidth: '4px', borderLeftColor: transaction.color }}
            >
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    transaction.type === 'expense'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {transaction.type === 'expense' ? 'Expense' : 'Income'}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    transaction.payment_method === 'cash'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-purple-100 text-purple-800'
                  }`}>
                    {transaction.payment_method}
                  </span>
                  {transaction.establishment_name && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                      {transaction.establishment_name}
                    </span>
                  )}
                  <span className="text-sm text-gray-500">{transaction.date}</span>
                </div>
                <p className="font-medium text-gray-900">{transaction.note}</p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className="text-xl font-bold text-gray-900">
                    {transaction.price} {transaction.currency}
                  </p>
                  <p className="text-sm text-gray-500">{transaction.price_dt.toFixed(2)} DT</p>
                </div>
                <button
                  onClick={() => deleteTransaction(transaction.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Create Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
              <h3 className="text-2xl font-bold mb-4">New Transaction</h3>
              <form onSubmit={createTransaction} className="space-y-4">
                {!isWorker && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Establishment *</label>
                    <select
                      required
                      value={newTransaction.establishment_id}
                      onChange={(e) => {
                        const est = establishments.find(x => x.id === e.target.value);
                        setNewTransaction({ ...newTransaction, establishment_id: e.target.value, establishment_name: est?.name || '' });
                      }}
                      className="w-full px-3 py-2 border rounded"
                      data-testid="transaction-establishment-select"
                    >
                      <option value="">Select Establishment</option>
                      {establishments.map((est) => (
                        <option key={est.id} value={est.id}>{est.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {isWorker && user?.establishment_name && (
                  <div className="bg-blue-50 text-blue-700 px-3 py-2 rounded text-sm">
                    Establishment: <strong>{user.establishment_name}</strong>
                  </div>
                )}

                {!isWorker && (
                  <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setNewTransaction({ ...newTransaction, type: 'expense' })}
                    className={`flex-1 py-2 rounded ${
                      newTransaction.type === 'expense'
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    Expense
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewTransaction({ ...newTransaction, type: 'income' })}
                    className={`flex-1 py-2 rounded ${
                      newTransaction.type === 'income'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    Income
                  </button>
                </div>
                )}

                <input
                  type="date"
                  required
                  value={newTransaction.date}
                  onChange={(e) => setNewTransaction({ ...newTransaction, date: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />

                <div className="flex space-x-2">
                  <input
                    type="number"
                    placeholder="Amount"
                    required
                    value={newTransaction.price}
                    onChange={(e) => setNewTransaction({ ...newTransaction, price: parseFloat(e.target.value) })}
                    className="flex-1 px-3 py-2 border rounded"
                  />
                  <select
                    value={newTransaction.currency}
                    onChange={(e) => setNewTransaction({ ...newTransaction, currency: e.target.value })}
                    className="px-3 py-2 border rounded"
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="DT">DT</option>
                  </select>
                </div>

                <select
                  value={newTransaction.payment_method}
                  onChange={(e) => setNewTransaction({ ...newTransaction, payment_method: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="cash">Cash</option>
                  <option value="online">Online</option>
                  <option value="card">Card</option>
                  <option value="transfer">Transfer</option>
                  <option value="booking">Booking.com</option>
                  <option value="airbnb">Airbnb</option>
                  <option value="d17">D17</option>
                </select>

                <textarea
                  placeholder="Note/Description"
                  required
                  value={newTransaction.note}
                  onChange={(e) => setNewTransaction({ ...newTransaction, note: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  rows="3"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                  <div className="flex flex-wrap gap-2">
                    {COLORS.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setNewTransaction({ ...newTransaction, color: color.value })}
                        className={`w-10 h-10 rounded-full border-2 ${
                          newTransaction.color === color.value
                            ? 'border-gray-900 scale-110'
                            : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.label}
                      />
                    ))}
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-600 text-white py-2 rounded hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Transactions;
